import axios from 'axios';
import axiosInstance from '../axiosInstance';

/**
 * 2days 팀 워크보드 (/2days/board/:slug) — Pre-join 을 마친 팀원만.
 * 응답은 모두 워크보드 전체(mockData.js 상단의 Workboard 모양)라 화면은 받은 값으로 바로 바꾼다.
 */
const data = (res) => res.data?.data ?? res.data;

export const getBoard = async (slug) =>
  data(
    await axiosInstance.get(`/two-days/boards/${slug}`, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

/** action 은 화면 reducer(model/actions.js)에 넘기는 것과 같은 모양 */
export const postBoardAction = async (slug, action) =>
  data(
    await axiosInstance.post(`/two-days/boards/${slug}/actions`, action, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

/** 운영진에게 보낸 내 메시지 — 팀원에게는 보이지 않는다 */
export const getOrganizerMessages = async (slug) =>
  data(
    await axiosInstance.get(`/two-days/boards/${slug}/organizer-messages`, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

export const sendOrganizerMessage = async (slug, message) =>
  data(
    await axiosInstance.post(`/two-days/boards/${slug}/organizer-messages`, message, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

/** 공지 전문 — 알림 글은 300자까지라 전체는 여기서 읽는다 */
export const getNotice = async (slug, id) =>
  data(
    await axiosInstance.get(`/two-days/boards/${slug}/notices/${id}`, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

/** Ops 메신저 — 내 대화 (팀원 ⇄ 운영진). send(taskId 선택) · read */
export const getChat = async (slug) =>
  data(
    await axiosInstance.get(`/two-days/boards/${slug}/chat`, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

export const postChat = async (slug, body) =>
  data(
    await axiosInstance.post(`/two-days/boards/${slug}/chat`, body, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

/** Complete project (동료 평가) 초안 조회·저장·제출 */
export const getCompleteProject = async (slug) =>
  data(
    await axiosInstance.get(`/two-days/boards/${slug}/complete-project`, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

export const saveCompleteProject = async (slug, draft) =>
  data(
    await axiosInstance.put(`/two-days/boards/${slug}/complete-project`, draft, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

export const submitCompleteProject = async (slug, draft) =>
  data(
    await axiosInstance.post(`/two-days/boards/${slug}/complete-project/submit`, draft, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

/** /2days 내 팀 보드 목록 — 스태프 계정이면 모든 팀 (access: member · staff) */
export const getMyBoards = async () =>
  data(await axiosInstance.get('/two-days/boards/mine', { skipGlobalError: true }));

/**
 * 태스크·결과물 첨부 올리기 — 서버에서 30분짜리 S3 주소를 받아 바로 PUT 한다. 돌려준 key 를 addTask 의 files 에 담는다.
 * @returns {Promise<string>} S3 key
 */
export const uploadBoardFile = async (slug, file, taskId, purpose) => {
  const { uploadUrl, key } = data(
    await axiosInstance.post(
      `/two-days/boards/${slug}/files/upload-url`,
      // taskId 가 있으면 담당자의 결과물 첨부, purpose=comment 면 댓글 사진, 없으면 리더의 Add task 첨부
      {
        fileName: file.name,
        size: file.size,
        ...(taskId ? { taskId } : {}),
        ...(purpose ? { purpose } : {}),
      },
      { skipGlobalError: true, skipLoading: true },
    ),
  );
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  return key;
};

/** Resources — 운영진이 올린 팀 파일 (Project brief 가 맨 위) */
export const getBoardResources = async (slug) =>
  data(
    await axiosInstance.get(`/two-days/boards/${slug}/resources`, {
      skipGlobalError: true,
      skipLoading: true,
    }),
  );

/** 완료 페이지 서비스 피드백 — 한 번만 (1,000자). 동료에게 보이지 않는다 */
export const postCompleteProjectFeedback = async (slug, feedback) =>
  data(
    await axiosInstance.post(
      `/two-days/boards/${slug}/complete-project/feedback`,
      { feedback },
      { skipGlobalError: true, skipLoading: true },
    ),
  );

/** 개발 → PM(QA) 인계 링크 확인 — 배포 링크 · 저장소 공개 · README 와 필수 섹션 (제출 전에 미리 본다) */
export const checkHandoffLinks = async (slug, web, repo) =>
  data(
    await axiosInstance.post(
      `/two-days/boards/${slug}/link-check`,
      { web, repo },
      { skipGlobalError: true, skipLoading: true },
    ),
  );
